<template>
  <div class="excel-tag-tab">
    <div class="excel-tag-tab__config">
      <el-empty
        v-if="!scopeReady"
        description="请返回上一步选择要打标的资源"
        :image-size="72"
      />

      <template v-else>
        <section
          ref="modeSection"
          class="excel-tag-tab__mode"
          :class="{ 'excel-tag-tab__mode--directGate': directUploadMode && !mode }"
        >
          <p v-if="directUploadMode && !mode" class="excel-tag-tab__directGateLead">
            您选择了直接上传已有打标表。请先选择与您表格一致的<strong>更新模式</strong>，系统会据此校验表头与操作列，再上传文件。
          </p>
          <div class="excel-tag-tab__modeRow">
            <span class="excel-tag-tab__modeLabel">更新模式</span>
            <el-radio-group v-model="mode" size="small">
              <el-radio
                v-for="item in modeOptions"
                :key="item.value"
                :label="item.value"
              >
                {{ item.label }}
                <el-tooltip :content="item.tip" placement="top" popper-class="genericResourceTooltip">
                  <i class="el-icon-question" />
                </el-tooltip>
              </el-radio>
            </el-radio-group>
            <span v-if="!mode && !directUploadMode" class="excel-tag-tab__modeHint">
              请先选择模式，再导出或上传
            </span>
          </div>
        </section>

        <section v-if="showUploadPanel" class="excel-tag-tab__uploadPanel">
          <div class="excel-tag-tab__uploadPanelHead">
            <div class="excel-tag-tab__uploadPanelIntro">
              <span class="excel-tag-tab__uploadPanelTitle">上传打标 Excel</span>
              <span class="excel-tag-tab__uploadPanelHint">
                <template v-if="!mode">选择更新模式后，可导出对应打标表并上传</template>
                <template v-else-if="directUploadMode">
                  {{ modeHint }} · 上传后将以表内 key 作为本次作用范围（共 {{ scopeKeySet.size || '—' }} 项）
                </template>
                <template v-else>
                  {{ modeHint }} · 共 {{ effectiveScopeCount }} 项资源
                </template>
              </span>
            </div>
            <div class="excel-tag-tab__uploadActions">
              <el-button
                size="small"
                :loading="catalogDownloading"
                icon="el-icon-download"
                @click="downloadTagCatalog"
              >
                下载 Tag 名录
              </el-button>
              <el-button
                size="small"
                :loading="downloading"
                :disabled="!mode || !canExportTemplate"
                icon="el-icon-download"
                @click="downloadTemplate"
              >
                导出打标表
              </el-button>
            </div>
          </div>

          <p v-if="directUploadMode" class="excel-tag-tab__directNote">
            直接上传模式：无需在第 1 步勾选资源，上传合法打标表后自动识别作用范围。
          </p>

          <el-upload
            drag
            action="/"
            :http-request="handleUpload"
            :show-file-list="false"
            :disabled="uploadBlocked"
            class="excel-tag-tab__upload"
          >
            <i class="el-icon-upload excel-tag-tab__uploadIcon" />
            <div class="el-upload__text">
              <template v-if="!mode">请先选择更新模式</template>
              <template v-else-if="uploadBlocked">请先勾选资源、导出打标表，或使用直接上传入口</template>
              <template v-else>将 xlsx 拖到此处，或<em>点击上传</em></template>
            </div>
          </el-upload>

          <div v-if="parseErrors.length" class="excel-tag-tab__feedback excel-tag-tab__feedback--error">
            <div>校验失败 {{ parseErrors.length }} 行</div>
            <div v-for="item in parseErrors.slice(0, 5)" :key="`${item.lineNo}-${item.resourceKey}-${item.reason}`">
              第 {{ item.lineNo }} 行：{{ item.reason }}
            </div>
          </div>
          <div v-else-if="parsedRows.length" class="excel-tag-tab__feedback excel-tag-tab__feedback--success">
            校验通过 {{ parsedRows.length }} 行 · 变更预览见下方
          </div>
        </section>
      </template>
    </div>

    <section
      v-if="showPreviewPanel"
      class="excel-tag-tab__preview"
      :class="{ 'excel-tag-tab__preview--collapsed': isPreviewEmpty }"
    >
      <tag-preview-panel
        :rows="previewRows"
        :clear-warnings="clearWarnings"
        :loading="previewLoading"
        :empty-hint="previewEmptyHint"
      />
    </section>
  </div>
</template>

<script>
import * as XLSX from 'xlsx'
import { exportTagTemplate, exportTagCatalog } from '../../../../api/tagManage'
import {
  TAG_BIND_MODE,
  TAG_BIND_MODE_OPTIONS,
  getModeLabel,
  getOperationHeaderForMode,
} from './constants'
import { buildExportPayload } from './scopeHelper'
import {
  canonicalizeExcelRow,
  parseExcelRows,
  validateExcelHeader,
  validateTagNamesInRows,
  extractAllKeysFromRows,
} from './excelParser'
import { loadTagNameRegistry } from './tagNameRegistry'
import TagPreviewPanel from './TagPreviewPanel.vue'

/** 取 workbook 首个 sheet；损坏/空文件时返回 null，避免 SheetNames[0] 为 undefined。 */
function resolveFirstWorksheet(workbook) {
  if (!workbook || !Array.isArray(workbook.SheetNames) || !workbook.SheetNames.length) {
    return null
  }
  const sheetName = workbook.SheetNames[0]
  return workbook.Sheets[sheetName] || null
}

export default {
  name: 'ExcelTagTab',
  components: { TagPreviewPanel },
  props: {
    selection: {
      type: Array,
      default: () => [],
    },
    filters: {
      type: Object,
      required: true,
    },
    resourceType: {
      type: Number,
      required: true,
    },
    filterSummary: {
      type: String,
      default: '',
    },
    hasFilters: {
      type: Boolean,
      default: false,
    },
    scopeReady: {
      type: Boolean,
      default: false,
    },
    /** 跳过第 1 步选资源：上传 xlsx 后由文件内 key 自证范围 */
    directUploadMode: {
      type: Boolean,
      default: false,
    },
    previewRows: {
      type: Array,
      default: () => [],
    },
    clearWarnings: {
      type: Array,
      default: () => [],
    },
    previewLoading: {
      type: Boolean,
      default: false,
    },
    previewEmptyHint: {
      type: String,
      default: '上传 Excel 并校验通过后，将在此显示变更 diff',
    },
    value: {
      type: Object,
      default: () => ({
        mode: '',
        parsedRows: [],
        scopeKeySet: [],
      }),
    },
  },
  data() {
    return this.createInitialState(this.value)
  },
  computed: {
    /**
     * 允许上传：已选模式，且（已导出/有 scope / 有勾选 / 直接上传模式）。
     */
    uploadBlocked() {
      if (!this.mode) {
        return true
      }
      if (this.directUploadMode) {
        return false
      }
      return !this.templateReady && !this.selection.length && !this.scopeKeySet.size
    },
    /** 向导路径须先选资源才能向后端导出打标表 */
    canExportTemplate() {
      return this.selection.length > 0
    },
    effectiveScopeCount() {
      if (this.scopeKeySet.size) {
        return this.scopeKeySet.size
      }
      return this.selection.length
    },
    modeHint() {
      if (!this.mode) {
        return ''
      }
      const opHeader = getOperationHeaderForMode(this.mode)
      if (this.mode === TAG_BIND_MODE.REPLACE) {
        return `已选「${getModeLabel(this.mode)}」：参考列多行对照，修改「${opHeader}」`
      }
      return `已选「${getModeLabel(this.mode)}」：仅填写「${opHeader}」`
    },
    /** 直接上传与常规路径均展示完整 Excel 页；未选模式时仅禁用上传，不隐藏面板 */
    showUploadPanel() {
      return true
    },
    showPreviewPanel() {
      return this.scopeReady
    },
    /**
     * 预览区是否为空(无数据、无加载、无覆盖告警)。
     * 空态时把预览区降级为自然高度,让 Excel 配置区(mode+上传)完整可见;
     * 有数据时再 flex:1 撑满剩余高度,el-table 接管纵向滚动。
     * 仅看 TagPreviewPanel 自己的 data-state 不够——这里要的是"用户视角空态",
     * 把"只是加载中"也归为"已有内容",避免空/有数据切换时高度跳变。
     */
    isPreviewEmpty() {
      return (
        !this.previewRows.length &&
        !this.previewLoading &&
        !(this.clearWarnings || []).length
      )
    },
  },
  watch: {
    mode(newVal, oldVal) {
      if (this._resetting) {
        return
      }
      if (oldVal && newVal !== oldVal) {
        this.parsedRows = []
        this.parseErrors = []
        this.exportedMode = ''
      }
      this.emitChange()
    },
    parsedRows() {
      if (this._resetting) {
        return
      }
      this.emitChange()
    },
    selection: {
      immediate: true,
      handler(rows) {
        if (!this.directUploadMode) {
          this.syncScopeFromSelection(rows)
        }
      },
    },
    scopeReady(val) {
      if (val && !this.directUploadMode) {
        this.syncScopeFromSelection(this.selection)
      }
    },
    directUploadMode(val) {
      if (val && this._resetting) {
        return
      }
      if (val) {
        this.parsedRows = []
        this.parseErrors = []
      }
    },
  },
  mounted() {
    if (!this.directUploadMode) {
      this.syncScopeFromSelection(this.selection)
    }
    this.ensureTagRegistry()
  },
  methods: {
    ensureTagRegistry() {
      return loadTagNameRegistry().catch(() => null)
    },
    syncScopeFromSelection(rows) {
      if (!rows || !rows.length) {
        // Med: 空 selection 时也清 templateReady,避免用户返回上一步取消资源勾选
        // 后再回到 Excel 步，upload 区域被错误的 templateReady 解锁。
        this.scopeKeySet = new Set()
        this.templateReady = false
        return
      }
      this.scopeKeySet = new Set(rows.map((row) => String(row.key || '').trim()).filter(Boolean))
      this.templateReady = true
      if (!this._resetting) {
        this.emitChange()
      }
    },
    createInitialState(value = {}) {
      const scopeKeys = value.scopeKeySet || []
      return {
        scopeKeySet: new Set(scopeKeys),
        templateReady: scopeKeys.length > 0,
        downloading: false,
        catalogDownloading: false,
        parsedRows: value.parsedRows || [],
        parseErrors: [],
        mode: value.mode || '',
        exportedMode: '',
        modeOptions: TAG_BIND_MODE_OPTIONS,
      }
    },
    resetWizard() {
      this._resetting = true
      Object.assign(this, this.createInitialState())
      if (!this.directUploadMode) {
        this.syncScopeFromSelection(this.selection)
      }
      this._resetting = false
    },
    /** 直接上传入口进入后，将视口滚至更新模式区，引导先选模式 */
    focusModeSection() {
      this.$nextTick(() => {
        const section = this.$refs.modeSection
        if (section && typeof section.scrollIntoView === 'function') {
          section.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
        }
      })
    },
    /**
     * 从 sessionStorage 草稿恢复 Excel 步：重建 scopeKeySet，允许在未重选资源时上传。
     */
    restoreFromDraft(draft = {}) {
      this._resetting = true
      const scopeKeys = draft.scopeKeySet || []
      this.mode = draft.mode || ''
      this.scopeKeySet = new Set(scopeKeys)
      this.templateReady = scopeKeys.length > 0
      this.parsedRows = []
      this.parseErrors = []
      this.exportedMode = draft.exportedMode || draft.mode || ''
      this._resetting = false
      this.emitChange()
    },
    resolveAllowedKeySet() {
      if (this.directUploadMode) {
        return null
      }
      if (this.scopeKeySet.size) {
        return this.scopeKeySet
      }
      if (this.selection.length) {
        return new Set(this.selection.map((row) => String(row.key || '').trim()).filter(Boolean))
      }
      return null
    },
    buildPayload() {
      const keySet = this.selection.length
        ? this.selection.map((row) => row.key)
        : undefined
      return {
        ...buildExportPayload(this.resourceType, this.filters, keySet),
        tagBindMode: this.mode,
      }
    },
    downloadTagCatalog() {
      this.catalogDownloading = true
      exportTagCatalog()
        .then((res) => {
          const blob = new Blob([res.data], {
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
          })
          const href = window.URL.createObjectURL(blob)
          const link = document.createElement('a')
          link.href = href
          const disposition = res.headers['content-disposition'] || ''
          const fileNameMatch = disposition.split('filename=')[1]
          link.download = fileNameMatch
            ? decodeURI(fileNameMatch)
            : 'Tag名录.xlsx'
          document.body.appendChild(link)
          link.click()
          document.body.removeChild(link)
          window.URL.revokeObjectURL(href)
          this.$message.success('Tag 名录已下载')
          return loadTagNameRegistry({ forceRefresh: true })
        })
        .catch((err) => {
          this.$message.error(err.message || err || 'Tag 名录下载失败')
        })
        .finally(() => {
          this.catalogDownloading = false
        })
    },
    downloadTemplate() {
      if (!this.mode) {
        this.$message.warning('请先选择更新模式')
        return
      }
      if (!this.canExportTemplate) {
        this.$message.warning('请先在第 1 步选择要打标的资源')
        return
      }
      this.downloading = true
      exportTagTemplate(this.buildPayload())
        .then((res) => {
          const blob = new Blob([res.data], {
            type: 'application/vnd.ms-excel',
          })
          const href = window.URL.createObjectURL(blob)
          const link = document.createElement('a')
          link.href = href
          link.download =
            decodeURI(
              (res.headers['content-disposition'] || '').split('filename=')[1]
            ) || `通用资源打标表-${getModeLabel(this.mode)}.xlsx`
          document.body.appendChild(link)
          link.click()
          document.body.removeChild(link)
          window.URL.revokeObjectURL(href)
          return this.readBlobKeys(blob)
        })
        .then((keys) => {
          this.scopeKeySet = keys
          this.templateReady = true
          this.exportedMode = this.mode
          this.$message.success(`打标表已导出（${getModeLabel(this.mode)}）`)
          this.emitChange()
          this.$emit('scope-established', {
            scopeKeySet: Array.from(this.scopeKeySet),
            mode: this.mode,
            exportedMode: this.exportedMode,
            source: 'export',
          })
        })
        .catch((err) => {
          this.$message.error(err || '打标表导出失败')
        })
        .finally(() => {
          this.downloading = false
        })
    },
    readBlobKeys(blob) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.onload = (event) => {
          try {
            const workbook = XLSX.read(event.target.result, { type: 'array' })
            const sheet = resolveFirstWorksheet(workbook)
            if (!sheet) {
              reject(new Error('Excel 文件无效或没有工作表'))
              return
            }
            const rows = XLSX.utils.sheet_to_json(sheet)
            const keys = rows
              .map((row) => {
                const canonical = canonicalizeExcelRow(row)
                return String(canonical.key || '').trim()
              })
              .filter(Boolean)
            resolve(new Set(keys))
          } catch (error) {
            reject(error)
          }
        }
        reader.onerror = reject
        reader.readAsArrayBuffer(blob)
      })
    },
    handleUpload(options) {
      if (!this.mode) {
        this.$message.warning('请先选择更新模式')
        return
      }
      if (this.uploadBlocked) {
        this.$message.warning('请先勾选资源、导出打标表，或使用直接上传入口')
        return
      }
      const file = options.file
      const reader = new FileReader()
      reader.onload = (event) => {
        const workbook = XLSX.read(event.target.result, { type: 'array' })
        const sheet = resolveFirstWorksheet(workbook)
        if (!sheet) {
          this.$message.error('Excel 文件无效或没有工作表')
          return
        }
        const header = this.getHeaderRow(sheet)
        const headerError = validateExcelHeader(header, this.mode)
        if (headerError) {
          this.$message.error(headerError)
          return
        }
        const rows = XLSX.utils.sheet_to_json(sheet)
        const fileDefinesScope = this.directUploadMode
        const allowedKeySet = fileDefinesScope ? null : this.resolveAllowedKeySet()
        const { parsedRows, errors } = parseExcelRows(rows, allowedKeySet, this.mode, {
          fileDefinesScope,
        })
        if (errors.length) {
          this.parsedRows = []
          this.parseErrors = errors
          this.$message.error(`校验失败 ${errors.length} 行`)
          this.emitChange()
          return
        }
        loadTagNameRegistry()
          .then((registry) => {
            const tagErrors = validateTagNamesInRows(
              parsedRows,
              registry.validNameSet,
              this.mode
            )
            if (tagErrors.length) {
              this.parsedRows = []
              this.parseErrors = tagErrors
              this.$message.error(`Tag 校验失败 ${tagErrors.length} 行`)
              this.emitChange()
              return
            }
            if (!parsedRows.length) {
              this.parsedRows = []
              this.parseErrors = []
              this.$message.warning('没有可提交的变更（操作列为空的行已跳过）')
              this.emitChange()
              return
            }
            if (fileDefinesScope) {
              this.scopeKeySet = extractAllKeysFromRows(rows)
              this.templateReady = true
            }
            this.parsedRows = parsedRows
            this.parseErrors = []
            this.$message.success(`解析成功 ${parsedRows.length} 行`)
            this.emitChange()
            this.$emit('parsed')
            if (fileDefinesScope || this.scopeKeySet.size) {
              this.$emit('scope-established', {
                scopeKeySet: Array.from(this.scopeKeySet),
                mode: this.mode,
                exportedMode: this.exportedMode || this.mode,
                source: fileDefinesScope ? 'direct-upload' : 'upload',
              })
            }
          })
          .catch((err) => {
            this.$message.error(err.message || err || 'Tag 库加载失败，无法校验 Tag 名称')
          })
      }
      reader.readAsArrayBuffer(file)
    },
    getHeaderRow(sheet) {
      const headers = []
      const range = XLSX.utils.decode_range(sheet['!ref'])
      for (let col = range.s.c; col <= range.e.c; col += 1) {
        const cell = sheet[XLSX.utils.encode_cell({ c: col, r: range.s.r })]
        if (cell && cell.t) {
          headers.push(XLSX.utils.format_cell(cell))
        }
      }
      return headers
    },
    validate() {
      if (!this.scopeReady) {
        return '请先选择要打标的资源，或使用直接上传'
      }
      if (!this.mode) {
        return '请先选择更新模式'
      }
      if (!this.parsedRows.length) {
        return '请先上传打标 Excel'
      }
      if (this.parseErrors.length) {
        return '上传数据存在错误，请修正后重试'
      }
      return ''
    },
    buildItems() {
      return this.parsedRows.map((row) => ({
        resourceKey: row.resourceKey,
        title: row.title,
        resourceType: row.resourceType,
        tagNames: row.tagNames,
      }))
    },
    emitChange() {
      const payload = {
        mode: this.mode,
        parsedRows: this.parsedRows,
        scopeKeySet: Array.from(this.scopeKeySet),
      }
      this.$emit('input', payload)
      this.$emit('change', payload)
    },
  },
}
</script>

<style scoped>
.excel-tag-tab {
  flex: 1 1 auto;
  min-height: 0;
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  padding: 0 4px 0 0;
  box-sizing: border-box;
}

.excel-tag-tab__config {
  /*
   * Med: min-height: 0 + max-height: 100% + overflow: auto,
   * 让 Excel 配置区(mode + 上传)在弹窗极矮时自身内部滚动,
   * 避免被预览区"反向挤压"导致整块超出弹窗视口、
   * 看不到 mode 单选/拖拽上传按钮。
   * 正常弹窗高度下(配置区自然高 < 父级剩余高度)不会触发滚动。
   */
  flex-shrink: 0;
  min-height: 0;
  max-height: 100%;
  overflow: auto;
  padding-right: 2px;
}

.excel-tag-tab__mode {
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-bottom: 10px;
  padding: 8px 12px;
  border-radius: 8px;
  background: var(--link-surface-muted, #f7f4ef);
  border: 1px solid var(--link-line-strong, #e5dfd6);
}

.excel-tag-tab__mode--directGate {
  padding: 14px 16px;
}

.excel-tag-tab__directGateLead {
  margin: 0;
  font-size: var(--link-text-md);
  line-height: 1.55;
  color: var(--link-ink-secondary);
}

.excel-tag-tab__directGateLead strong {
  font-weight: var(--link-font-semibold);
  color: var(--link-ink-emphasis);
}

.excel-tag-tab__modeRow {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
}

.excel-tag-tab__modeLabel {
  flex-shrink: 0;
  font-size: var(--link-text-md);
  font-weight: var(--link-font-semibold);
  color: var(--link-ink);
}

.excel-tag-tab__modeHint {
  font-size: var(--link-text-sm);
  color: var(--link-ink-muted);
}

.excel-tag-tab__uploadPanel {
  padding: 12px 14px 14px;
  border-radius: 8px;
  background: var(--link-workspace, #f3f0ea);
  border: 1px solid var(--link-line-strong, #e5dfd6);
}

.excel-tag-tab__uploadPanelHead {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 10px;
}

.excel-tag-tab__uploadPanelIntro {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
  flex: 1 1 auto;
}

.excel-tag-tab__uploadActions {
  display: flex;
  flex-shrink: 0;
  flex-wrap: wrap;
  gap: 8px;
  justify-content: flex-end;
}

.excel-tag-tab__uploadPanelTitle {
  font-size: var(--link-text-md);
  font-weight: var(--link-font-semibold);
  color: var(--link-ink);
}

.excel-tag-tab__uploadPanelHint {
  font-size: var(--link-text-sm);
  color: var(--link-ink-muted);
  line-height: 1.45;
}

.excel-tag-tab__directNote {
  margin: 0 0 10px;
  font-size: var(--link-text-sm);
  color: var(--link-ink-secondary);
  line-height: 1.45;
}

.excel-tag-tab__upload {
  display: block;
  width: 100%;
}

.excel-tag-tab__upload >>> .el-upload {
  display: block;
  width: 100%;
}

.excel-tag-tab__upload >>> .el-upload-dragger {
  width: 100%;
  height: 108px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  border-color: var(--link-line-strong, #e5dfd6);
  background: var(--link-surface, #faf8f5);
}

.excel-tag-tab__upload >>> .el-upload-dragger:hover {
  border-color: var(--link-accent);
}

.excel-tag-tab__upload >>> .el-upload-dragger.is-disabled {
  cursor: not-allowed;
  opacity: 0.72;
}

.excel-tag-tab__uploadIcon {
  margin: 0 0 6px;
  font-size: var(--link-icon-empty);
  color: var(--link-ink-muted);
  line-height: 1;
}

.excel-tag-tab__upload >>> .el-upload__text {
  font-size: var(--link-text-md);
  color: var(--link-ink-secondary);
}

.excel-tag-tab__feedback {
  margin-top: 10px;
  padding: 8px 10px;
  border-radius: 6px;
  font-size: var(--link-text-sm);
  line-height: 1.5;
}

.excel-tag-tab__feedback--error {
  background: var(--link-status-error-bg);
  border: 1px solid var(--link-status-error-border);
  color: var(--link-status-error-ink);
}

.excel-tag-tab__feedback--success {
  background: var(--link-status-success-bg);
  border: 1px solid var(--link-status-success-border);
  color: var(--link-status-success-ink);
}

.excel-tag-tab__preview {
  /*
   * Med: flex:1 1 auto 占据父级剩余高度,overflow:auto 接管滚动。
   * el-table 在这里按行数自然撑大(不再有 max-height 限制),
   * 行少时整面板矮,行多时整面板高;只有当表格高度 > 弹窗剩余高度
   * 时,滚动条才出现在本容器的边界(用户视觉上能看到)。
   *
   * --collapsed 变体:空态(未上传/未生成 preview)时降级为自然高度,
   * 避免预览区把 Excel 配置区(mode + 上传)挤出弹窗视口。
   * 与 tag-preview-panel--empty 联动,整面板只占"标题+一行提示"的高度。
   */
  flex: 1 1 auto;
  min-height: 0;
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid var(--link-line-strong, #e5dfd6);
  overflow: auto;
  display: flex;
  flex-direction: column;
}

.excel-tag-tab__preview--collapsed {
  flex: 0 0 auto;
  overflow: visible;
  margin-top: 8px;
  padding-top: 8px;
  border-top-style: dashed;
}
</style>
